import dayjs from 'dayjs';

import { useMemo } from 'react';

import { ACTION_OBJECT_TYPES } from 'actions/useActionsLibrary';

import DynamicList from 'components/list/DynamicList';
import { FilterAction, FilterType, ListComponents } from 'components/list/DynamicListFilterUtils';

import useRouterFilters from 'hooks/useRouterFilters';

import { ENUMS, ORDERS } from 'services/consts';

import { DYNAMICLISTS } from 'utils/constants';

const BulletTrackingList = () => {
	const defaultSort = useMemo(() => [{ id: 'shipmentCreatedDate', desc: true }], []);

	const columns = useMemo(
		() => [
			{
				Header: 'ID',
				accessor: 'ID',
				filterType: FilterType.number,
				showOnMobile: true,
				show: false,
				showCopyButton: true
			},
			{
				Header: 'orderID',
				accessor: 'orderID',
				filterType: FilterType.string,
				width: 70,
				showCopyButton: true
			},
			{
				Header: 'shipmentNumber',
				accessor: 'shipmentNumber',
				width: 100,
				showCopyButton: true,
				filterType: FilterType.string
			},
			{
				Header: 'newOrderDate',
				accessor: 'newOrderDate',
				filterType: FilterType.date,
				width: 100,
				renderCell: ListComponents.DATETIME,
				show: false
			},
			{
				Header: 'clientName',
				accessor: 'clientName',
				filterType: FilterType.string
			},
			{
				Header: 'companyId',
				accessor: 'companyId',
				filterType: FilterType.number
			},
			{
				Header: 'originCountry',
				accessor: 'originCountry',
				filterType: FilterType.enumMulti,
				enumName: ENUMS.COUNTRIES,
				filterPrefix: 'country.',
				renderCell: ListComponents.COUNTRY,
				show: false
			},
			{
				Header: 'branchName',
				accessor: 'branchName',
				filterType: FilterType.string,
				show: false
			},
			{
				Header: 'warehouseName',
				accessor: 'warehouseName',
				filterType: FilterType.string,
				show: false
			},
			{
				Header: 'originCity',
				accessor: 'originCity',
				filterType: FilterType.string,
				show: false
			},
			{
				Header: 'destinationCity',
				accessor: 'destinationCity',
				filterType: FilterType.string
			},
			{
				Header: 'DCSettingID',
				accessor: 'dcSettingID',
				filterType: FilterType.number,
				show: false
			},
			{
				Header: 'DCSettingName',
				accessor: 'dcSettingName',
				filterType: FilterType.string,
				show: false
			},
			{
				Header: 'deliveryCompany',
				accessor: 'deliveryCompanyName',
				filterType: FilterType.string
			},
			{
				Header: 'driverName',
				accessor: 'driverName',
				filterType: FilterType.string,
				width: 100,
				show: false
			},
			{
				Header: 'status',
				accessor: 'status',
				filterType: FilterType.enumMulti,
				enumName: ENUMS.ALL_STATUSES,
				renderCell: ListComponents.SHIPMENT_STATUS,
				showOnMobile: true
			},
			{
				Header: 'isCompleted',
				accessor: 'isCompleted',
				filterType: FilterType.enumMulti,
				enumName: ENUMS.YES_NO,
				renderCell: ListComponents.STATUS,
				show: false
			},
			{
				Header: 'shipmentCreatedDate',
				accessor: 'shipmentCreatedDate',
				filterType: FilterType.date,
				width: 100,
				renderCell: ListComponents.DATETIME,
				filterRemovable: false
			},
			{
				Header: 'timeSinceCreation',
				accessor: 'timeSinceCreation',
				filterType: FilterType.float
			},
			{
				Header: 'pickupDate',
				accessor: 'pickupDate',
				filterType: FilterType.date,
				renderCell: ListComponents.DATETIME
			},
			{
				Header: 'pickupTime',
				accessor: 'pickupTime',
				filterType: FilterType.float
			},
			{
				Header: 'pickupSLATime',
				accessor: 'pickupSLATime',
				filterType: FilterType.date,
				renderCell: ListComponents.DATETIMEWITHOUTLOCALE
			},
			{
				Header: 'pickupSLASuccess',
				accessor: 'pickupSLASuccess',
				filterType: FilterType.enumMulti,
				enumName: ENUMS.YES_NO,
				renderCell: ListComponents.STATUS,
				show: true
			},
			{
				Header: 'timeSincePickup',
				accessor: 'timeSincePickup',
				filterType: FilterType.float
			},
			{
				Header: 'deliveryDate',
				accessor: 'deliveryDate',
				filterType: FilterType.date,
				width: 100,
				renderCell: ListComponents.DATETIME
			},
			{
				Header: 'deliveryTimeSincePickup',
				accessor: 'deliveryTimeSincePickup',
				filterType: FilterType.float
			},
			{
				Header: 'totalDeliveryTime',
				accessor: 'totalDeliveryTime',
				filterType: FilterType.float
			},
			{
				Header: 'deliverySLATime',
				accessor: 'deliverySLATime',
				filterType: FilterType.date,
				width: 100,
				renderCell: ListComponents.DATETIMEWITHOUTLOCALE
			},
			{
				Header: 'deliverySLASuccess',
				accessor: 'deliverySLASuccess',
				filterType: FilterType.enumMulti,
				enumName: ENUMS.YES_NO,
				renderCell: ListComponents.STATUS,
				show: true
			},
			{
				Header: 'agreementType',
				accessor: 'agreementType',
				filterType: FilterType.enumMulti,
				width: 100,
				enumName: ENUMS.SHIPPING_PRICE_AGREEMENT_TYPE
			},
			{
				Header: 'returnDate',
				accessor: 'returnDate',
				filterType: FilterType.date,
				width: 100,
				renderCell: ListComponents.DATETIME
			},
			{
				Header: 'totalReturnTime',
				accessor: 'totalReturnTime',
				filterType: FilterType.float
			},
			{
				Header: 'shipmentCanceledDate',
				accessor: 'shipmentCanceledDate',
				filterType: FilterType.date,
				width: 100,
				renderCell: ListComponents.DATETIME,
				show: false
			},
			{
				Header: 'modifiedDate',
				accessor: 'modifiedDate',
				filterType: FilterType.date,
				width: 100,
				renderCell: ListComponents.DATETIME
			}
		],
		[]
	);

	const defaultFilters = useMemo(
		() => [
			{
				column: columns.find((c) => c.accessor === 'shipmentCreatedDate'),
				action: FilterAction.between,
				value: {
					start: dayjs().subtract(1, 'day').endOf('day').toISOString(),
					end: dayjs().endOf('day').toISOString()
				}
			}
		],
		[columns]
	);

	const filtersFromRouter = useRouterFilters(columns);

	return (
		<DynamicList
			id={DYNAMICLISTS.BULLET_TRACKING_LIST}
			columns={columns}
			fetchUrl={ORDERS.GET_BULLET_ORDERS}
			actionObjectType={ACTION_OBJECT_TYPES.ORDER}
			defaultSort={defaultSort}
			defaultFilters={filtersFromRouter ?? defaultFilters}
			infiniteScroll
			disableInfiniteScrollUp
			exportName="bulletTrackingList"
		/>
	);
};

export default BulletTrackingList;
